import { Gender, Speaker } from "src/app/modules/speech2text/models/speaker.model";

export interface SpeechSegment {
  endTime: number;
  startTime: number;
  speakerId: string;
  text: string;
  confidence?: number;
  translatedTexts?: Record<string, string>;
  speaker: Speaker;
  originalIndex?: number;
}

export const segmentsToStringByLanguage = (segments: SpeechSegment[][], language: string): string => {
  return segments
    .map((subSegments) => {
      const startTime = subSegments[0]?.startTime;
      const endTime = subSegments.at(-1)?.endTime;
      const startTimeString = startTime ? new Date(startTime * 1000).toISOString().slice(11, -5) : "";
      const endTimeString = endTime ? new Date(endTime * 1000).toISOString().slice(11, -5) : "";
      return `${subSegments[0]?.speaker.name} (${startTimeString} - ${endTimeString}):\n${subSegments
        .map((segment) => {
          return segment.translatedTexts?.[language] || segment.text;
        })
        .join(" ")}`;
    })
    .join("\n");
};

export const segmentsToString = (segments: SpeechSegment[][], language?: string): string => {
  const maxTime = segments.at(-1)?.at(-1)?.endTime;
  const sliceStart = maxTime && maxTime > 3600 ? 11 : 14; // whether to display hh:mm:ss or mm:ss
  let text = "";
  for (const segment of segments) {
    text += `${segment[0]?.speaker.name}:\n`;
    for (const s of segment) {
      let segmentText = s.text;
      if (language && s.translatedTexts && Object.hasOwn(s.translatedTexts, language)) {
        segmentText = s.translatedTexts[language]!;
      }
      text += `${new Date(s.startTime * 1000).toISOString().slice(sliceStart, 19)} ${segmentText}\n`;
    }
    text += "\n";
  }
  return text;
};

export const formatSegmentsForCopy = (segments: SpeechSegment[][], language?: string): InnerHTML => {
  let text = "";
  for (const segment of segments) {
    let segmentText = "";
    let startTime = 0;
    for (const s of segment) {
      if (startTime === 0) {
        startTime = s.startTime;
      }
      if (language && s.translatedTexts && Object.hasOwn(s.translatedTexts, language)) {
        segmentText += s.translatedTexts[language]!;
      } else {
        segmentText += s.text;
      }
      segmentText += " ";
    }
    text += `<b>[${new Date(startTime * 1000).toISOString().slice(11, 23)} - ${
      segment[0]?.speaker.name
    }]</b>\n<br>${segmentText}\n\n<br><br>`;
    text += "\n";
  }
  return { innerHTML: text };
};

const getName = (gender: Gender, index: number): string => {
  if (index === 0) {
    return $localize`:@@speech_segment_speaker_not_identified:Non identifié`;
  }
  if (gender === "UNKNOWN") {
    return $localize`:@@speech_segment_gender_unknown_speaker:Locuteurice ${index}`;
  }
  if (gender === "FEMALE") {
    return $localize`:@@speech_segment_female_speaker:Locutrice ${index}`;
  }
  return $localize`:@@speech_segment_male_speaker:Locuteur ${index}`;
};

export const aggSegmentsBySpeaker = (inSegmentArray: SpeechSegment[]): SpeechSegment[][] => {
  return inSegmentArray.reduce<SpeechSegment[][]>((segmentJaggedArray, segment, index) => {
    // Add the original index to the segment
    const segmentWithIndex = { ...segment, originalIndex: index };

    const lastSegmentArray = segmentJaggedArray[segmentJaggedArray.length - 1];
    if (lastSegmentArray && segment.speakerId === lastSegmentArray[0]?.speakerId) {
      lastSegmentArray.push(segmentWithIndex);
    } else {
      segmentJaggedArray.push([segmentWithIndex]);
    }
    return segmentJaggedArray;
  }, []);
};

const getIndex = (speakerIndex: number, gender: Gender): number => {
  if (gender === "UNKNOWN") {
    return speakerIndex;
  }
  if (gender === "FEMALE") {
    return speakerIndex * 2 - 1;
  }
  return speakerIndex * 2;
};

const getGender = (speakerId?: string): Gender => {
  if (speakerId?.startsWith("M")) {
    return "MALE";
  }
  if (speakerId?.startsWith("F")) {
    return "FEMALE";
  }
  return "UNKNOWN";
};

export const deserializeToSpeechSegment = (
  input: Omit<SpeechSegment, "speaker"> & { speaker?: Speaker },
): SpeechSegment => {
  const speakerIndex = parseInt(input.speakerId.substring(2), 10);
  const gender = input.speaker?.gender ?? getGender(input.speakerId);
  const index = isNaN(speakerIndex) ? 0 : getIndex(speakerIndex, gender);
  return {
    ...input,
    speaker: {
      id: input.speaker?.id ?? input.speakerId,
      index,
      gender,
      name: input.speaker?.name ?? getName(gender, index),
    },
  };
};
